import React, { useState, useEffect, useRef } from 'react';
import { DialogSection, DialogField, DialogOption, VariableAction, VariableCondition, DialogVariable, fetchDialog, getCachedDialogs } from '@/lib/stores/dialogStore';
import { resolveTypeElement, getInitialValue, isValueInRange, getTypeItemById } from '@/lib/utils/typeStoreResolver';
import { DialogFieldPlayDisplay } from './DialogDisplay/DialogFieldPlayDisplay';
import { Button } from '@/components/ui/button';
import { IconProvider } from '@/components/ui';
import { DialogFieldPrevDisplay } from './DialogDisplay/DialogFieldPrevDisplay';
import { useToast } from '@/hooks/use-toast';

interface DialogPlayViewProps {
  sections: DialogSection[];
  currentSectionIndex: number;
  speakerMode?: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  speakerTemplateIds?: string[];
  dialogVariables?: DialogVariable[];
  gameId?: string;
  onSectionChange: (index: number) => void;
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onDialogSwitch?: (dialogId: string, sectionIdentifier?: string | null, dialogName?: string) => Promise<void>;
}

interface DialogContext {
  sections: DialogSection[];
  speakerMode: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId: string | null;
  speakerTemplateIds: string[];
  dialogVariables: DialogVariable[];
  dialogId?: string;
  dialogName?: string;
}

interface ConditionEvalResult {
  variableName: string;
  variableValue: boolean | number | string;
  matchedOperator?: string;
  matchedValue?: boolean | number | string;
  /** Human-readable label for the matched value (e.g., range element name like "Jill" instead of an ID) */
  matchedLabel?: string;
  matched: boolean;
}

interface SetterEvalResult {
  variableName: string;
  operator: string;
  setValue: boolean | number | string;
  newValue: boolean | number | string;
}

interface VisitedField {
  sectionIndex: number;
  fieldIndex: number;
  section: DialogSection;
  field: DialogField;
  conditionResult?: ConditionEvalResult;
  setterResult?: SetterEvalResult;
}

export function DialogPlayView({
  sections: initialSections,
  currentSectionIndex: initialSectionIndex,
  speakerMode: initialSpeakerMode = 'none',
  speakerTypeId: initialSpeakerTypeId,
  speakerTemplateIds: initialSpeakerTemplateIds,
  dialogVariables: initialDialogVariables,
  gameId,
  onSectionChange,
  onUpdateField,
  onDialogSwitch,
}: DialogPlayViewProps) {
  const { toast } = useToast();
  
  // Active dialog context - can be the original or an external dialog in play mode
  const [activeDialogContext, setActiveDialogContext] = useState<DialogContext>({
    sections: initialSections,
    speakerMode: initialSpeakerMode,
    speakerTypeId: initialSpeakerTypeId || null,
    speakerTemplateIds: initialSpeakerTemplateIds || [],
    dialogVariables: initialDialogVariables || [],
  });
  
  const [visitedSections, setVisitedSections] = useState<number[]>([0]);
  const [isComplete, setIsComplete] = useState(false);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [visitedFields, setVisitedFields] = useState<VisitedField[]>([]);
  const [isCurrentFieldTyping, setIsCurrentFieldTyping] = useState(true);
  const [animationEnabled, setAnimationEnabled] = useState(true);
  const [isLoadingDialog, setIsLoadingDialog] = useState(false);
  const [variables, setVariables] = useState<Record<string, boolean | number | string>>({});
  const variablesRef = useRef<Record<string, boolean | number | string>>({});
  const [showVariablesDebug, setShowVariablesDebug] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  // Track whether the current section was reached via an explicit jump (condition, option, setter, field next_section)
  // vs. linear progression. When a jumped-to section reaches a dead end (no outgoing connection), end the dialog.
  const reachedViaJump = useRef(false);
  
  // Keep ref in sync with state
  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  // Use active context sections
  const sections = activeDialogContext.sections;
  const speakerMode = activeDialogContext.speakerMode;
  const speakerTypeId = activeDialogContext.speakerTypeId;
  const speakerTemplateIds = activeDialogContext.speakerTemplateIds;
  const dialogVariables = activeDialogContext.dialogVariables;
  const currentSection = sections[currentSectionIndex];
  // Sync active dialog context when props change (but only if not in the middle of playing)
  useEffect(() => {
    setActiveDialogContext({
      sections: initialSections,
      speakerMode: initialSpeakerMode,
      speakerTypeId: initialSpeakerTypeId || null,
      speakerTemplateIds: initialSpeakerTemplateIds || [],
      dialogVariables: initialDialogVariables || [],
    });
  }, [initialSections, initialSpeakerMode, initialSpeakerTypeId, initialSpeakerTemplateIds, initialDialogVariables]);

  // Initialize variables from declarations on mount and when new variables are declared
  useEffect(() => {
    // Don't reset if dialogVariables becomes empty (might be a temporary state issue)
    if (!dialogVariables || dialogVariables.length === 0) {
      return;
    }
    
    setVariables(prev => {
      const updated = { ...prev };
      
      // Add any new variables from declarations that don't exist yet
      if (dialogVariables) {
        dialogVariables.forEach(v => {
          if (v.name && v.name.trim()) {
            if (!(v.name in updated)) {
              // Resolve TypeStore reference if present
              if (v.typeStoreRef) {
                const resolved = resolveTypeElement(v.typeStoreRef.typeId, v.typeStoreRef.elementId);
                updated[v.name] = resolved ? getInitialValue(resolved.element) : v.initialValue;
              } else {
                updated[v.name] = v.initialValue;
              }
            }
          }
        });
      }
      
      return updated;
    });
  }, [dialogVariables]);

  // Helper function to set a variable value
  const setVariable = (name: string, value: boolean | number | string) => {
    // Update ref immediately so condition evaluations always see the latest value
    // (useEffect-based sync runs after render, which can cause race conditions)
    variablesRef.current = { ...variablesRef.current, [name]: value };
    setVariables(prev => ({ ...prev, [name]: value }));
  };

  // Helper function to execute a variable action
  const executeVariableAction = (action: VariableAction) => {
    // Find the variable declaration to get its type and configuration
    const varDeclaration = dialogVariables?.find(v => v.name === action.variable);
    if (!varDeclaration) {
      console.warn(`Variable declaration not found for: ${action.variable}`);
      return;
    }

    const currentValue = variables[action.variable] ?? varDeclaration.initialValue;
    let newValue: boolean | number | string;

    // Handle based on variable type
    if (varDeclaration.type === 'text') {
      // Text variables only support 'set' operation
      if (action.operator === 'set') {
        newValue = String(action.value);
      } else {
        console.warn(`Operation ${action.operator} not supported for text variables`);
        return;
      }
    } else if (varDeclaration.type === 'range') {
      // Range variables respect min/max bounds
      let numValue = typeof currentValue === 'number' ? currentValue : 0;
      
      switch (action.operator) {
        case 'set':
          numValue = Number(action.value);
          break;
        case 'increment':
          numValue += (varDeclaration.increment || 1);
          break;
        case 'decrement':
          numValue -= (varDeclaration.increment || 1);
          break;
        default:
          return;
      }
      
      // Clamp to range if enforceRange is true (default)
      if (action.enforceRange !== false) {
        const min = varDeclaration.rangeMin ?? 0;
        const max = varDeclaration.rangeMax ?? 100;
        numValue = Math.max(min, Math.min(max, numValue));
      }
      
      newValue = numValue;
    } else if (varDeclaration.type === 'boolean') {
      // Boolean variables only support 'set' operation
      if (action.operator === 'set') {
        newValue = Boolean(action.value);
      } else {
        console.warn(`Operation ${action.operator} not supported for boolean variables`);
        return;
      }
    } else {
      // Number variables support all operations
      switch (action.operator) {
        case 'set':
          newValue = action.value;
          break;
        case 'increment':
          newValue = Number(currentValue) + Number(action.value);
          break;
        case 'decrement':
          newValue = Number(currentValue) - Number(action.value);
          break;
        default:
          return;
      }
    }

    setVariable(action.variable, newValue);
  };

  // Helper function to evaluate a condition
  const evaluateCondition = (condition: VariableCondition): boolean => {
    // Use ref to get latest variables value, avoiding stale closures
    const varValue = variablesRef.current[condition.variable];
    
    // Handle undefined variable
    if (varValue === undefined) {
      console.warn(`Variable ${condition.variable} not found in current context`);
      return false;
    }
    
    // Handle in_range operator for range-selector variables
    if (condition.operator === 'in_range') {
      // Find the variable declaration to get the TypeItem reference
      const varDeclaration = dialogVariables?.find(v => v.name === condition.variable);
      if (!varDeclaration || !varDeclaration.typeStoreRef?.typeId) {
        console.warn(`Range selector variable ${condition.variable} has no TypeItem reference`);
        return false;
      }
      
      // Get the TypeItem and find the element
      const typeItem = getTypeItemById(varDeclaration.typeStoreRef.typeId);
      if (!typeItem || !typeItem.data) {
        console.warn(`TypeItem ${varDeclaration.typeStoreRef.typeId} not found`);
        return false;
      }
      
      // Find the range element by ID (condition.value is the elementId)
      const rangeElement = typeItem.data.find(el => el.id === String(condition.value));
      if (!rangeElement) {
        console.warn(`Range element ${condition.value} not found in TypeItem`);
        return false;
      }
      
      // Check if the current value falls within this range
      return isValueInRange(Number(varValue), rangeElement);
    }
    
    // For boolean variables, return the variable's value directly
    // (true goes to TRUE branch, false goes to FALSE branch)
    if (typeof condition.value === 'boolean') {
      return Boolean(varValue);
    }
    
    // For string/text variables, use text-specific operators
    if (typeof varValue === 'string' || typeof condition.value === 'string') {
      const strValue = String(varValue);
      const compareValue = String(condition.value);
      
      switch (condition.operator) {
        case '==':
          return strValue === compareValue;
        case '!=':
          return strValue !== compareValue;
        case 'contains':
          return strValue.includes(compareValue);
        case 'startsWith':
          return strValue.startsWith(compareValue);
        case 'endsWith':
          return strValue.endsWith(compareValue);
        // Numeric comparisons don't make sense for text
        case '>':
        case '<':
        case '>=':
        case '<=':
          console.warn(`Operator ${condition.operator} not supported for text variables`);
          return false;
        default:
          return false;
      }
    }
    
    // For number variables (and range), use numeric operator comparison
    switch (condition.operator) {
      case '==':
        return varValue === condition.value;
      case '!=':
        return varValue !== condition.value;
      case '>':
        return Number(varValue) > Number(condition.value);
      case '<':
        return Number(varValue) < Number(condition.value);
      case '>=':
        return Number(varValue) >= Number(condition.value);
      case '<=':
        return Number(varValue) <= Number(condition.value);
      // Text operators don't make sense for numbers
      case 'contains':
      case 'startsWith':
      case 'endsWith':
        console.warn(`Operator ${condition.operator} not supported for numeric variables`);
        return false;
      default:
        return false;
    }
  };

  // Reset completion state and field index when section changes
  useEffect(() => {
    setIsComplete(false);
    setCurrentFieldIndex(0);
  }, [currentSectionIndex]);

  // Reset typing state when field changes
  useEffect(() => {
    setIsCurrentFieldTyping(true);
  }, [currentFieldIndex, currentSectionIndex]);

  // Auto-scroll to bottom when new content appears - with smooth animation
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [visitedFields, currentFieldIndex]);

  if (!currentSection) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-lg text-gray-600">No sections available</p>
          <p className="text-sm text-gray-500 mt-2">Add sections in Edit Mode to play through the dialog</p>
        </div>
      </div>
    );
  }

  // Get current field being displayed
  const currentField = currentSection?.fields?.[currentFieldIndex];
  const totalFields = currentSection?.fields?.length || 0;
  const isLastField = currentFieldIndex === totalFields - 1;
  const isLastFieldQuestion = currentField?.type === 'question';

  // Helper function to continue to next field or next section
  const continueToNextFieldOrSection = () => {
    if (currentFieldIndex < totalFields - 1) {
      // Go to next field in current section
      setCurrentFieldIndex(currentFieldIndex + 1);
    } else {
      // Last field in section, move to next section
      handleNextSection();
    }
  };

  // Handle progressing to next field within the section
  const handleNextField = () => {
    // Add current field to visited fields
    if (currentField && currentSection) {
      setVisitedFields(prev => [...prev, {
        sectionIndex: currentSectionIndex,
        fieldIndex: currentFieldIndex,
        section: currentSection,
        field: currentField,
      }]);
    }

    // If current field is end-text or end-dialog, complete the dialog
    if (currentField?.type === 'end-text' || currentField?.type === 'end-dialog') {
      setIsComplete(true);
      return;
    }

    continueToNextFieldOrSection();
  };

  // Handle option click (branching logic)
  const handleOptionClick = async (option: DialogOption, optionIndex: number) => {
    // Execute variable action if defined
    if (option.variableAction) {
      executeVariableAction(option.variableAction);
    }

    // Add current field to visited fields
    if (currentField && currentSection) {
      setVisitedFields(prev => [...prev, {
        sectionIndex: currentSectionIndex,
        fieldIndex: currentFieldIndex,
        section: currentSection,
        field: currentField,
      }]);
    }

    // If current field is end-text or end-dialog, complete the dialog
    if (currentField?.type === 'end-text' || currentField?.type === 'end-dialog') {
      setIsComplete(true);
      return;
    }

    // Check if this option jumps to another dialog
    if (option.next_dialog) {
      // Load external dialog data locally in play mode without switching the parent dialog
      try {
        setIsLoadingDialog(true);
        
        if (!gameId) {
          throw new Error('Game ID is required to load external dialogs');
        }
        
        // Fetch the target dialog
        const targetDialog = await fetchDialog(option.next_dialog, gameId);
        
        // Get dialog name for display
        const dialogName = targetDialog?.name || 'Unknown Dialog';
        
        // Update sections from the new dialog
        let newSections = targetDialog.data?.sections || [];
        
        // Ensure sections have stable IDs
        newSections = newSections.map((section, index) => ({
          ...section,
          id: section.id || `section_${Date.now()}_${index}`,
        }));
        
        // Find target section index
        let targetSectionIndex = 0;
        if (option.next_dialog_section) {
          const foundIndex = newSections.findIndex(s => s.section_identifier === option.next_dialog_section);
          if (foundIndex !== -1) {
            targetSectionIndex = foundIndex;
          }
        }
        
        // Update active dialog context for play mode only
        setActiveDialogContext({
          sections: newSections,
          speakerMode: targetDialog.data?.speakerMode || 'none',
          speakerTypeId: targetDialog.data?.speakerTypeId || null,
          speakerTemplateIds: targetDialog.data?.speakerTemplateIds || [],
          dialogVariables: targetDialog.data?.variables || [],
          dialogId: option.next_dialog,
          dialogName: dialogName,
        });
        
        // Keep history for fluent conversation - only reset current position
        reachedViaJump.current = true;
        setCurrentSectionIndex(targetSectionIndex);
        setCurrentFieldIndex(0);
        setIsComplete(false);
        
      } catch (error) {
        console.error('Failed to load external dialog:', error);
        toast({
          title: 'Error',
          description: 'Failed to load the target dialog',
          variant: 'destructive',
        });
      } finally {
        setIsLoadingDialog(false);
      }
      return;
    }

    // If next_section is specified, navigate to that section
    if (option.next_section) {
      const targetSectionIndex = sections.findIndex(
        s => s.section_identifier === option.next_section
      );
      if (targetSectionIndex !== -1) {
        reachedViaJump.current = true;
        setCurrentSectionIndex(targetSectionIndex);
        setVisitedSections(prev => [...prev, targetSectionIndex]);
      } else {
        console.warn(`Section "${option.next_section}" not found`);
        // Default to continue to next field/section
        continueToNextFieldOrSection();
      }
    } else {
      // No next_section specified, continue to next field or next section
      continueToNextFieldOrSection();
    }
  };

  // Handle next section (linear navigation)
  const handleNextSection = () => {
    // If the current section was reached via an explicit jump (condition branch, option,
    // setter, or field next_section) and the section's fields have no outgoing connection,
    // this is a dead end in the flow — end the dialog instead of continuing linearly.
    if (reachedViaJump.current) {
      setIsComplete(true);
      return;
    }
    
    if (currentSectionIndex < sections.length - 1) {
      const nextIndex = currentSectionIndex + 1;
      reachedViaJump.current = false; // linear progression
      setCurrentSectionIndex(nextIndex);
      setVisitedSections(prev => [...prev, nextIndex]);
    } else {
      // Reached the end
      setIsComplete(true);
    }
  };

  // Handle going back (either to previous field or previous section)
  const handleBack = () => {
    // Remove last visited field
    if (visitedFields.length > 0) {
      const lastVisited = visitedFields[visitedFields.length - 1];
      setVisitedFields(prev => prev.slice(0, -1));
      
      // Set current position to the last visited field
      setCurrentSectionIndex(lastVisited.sectionIndex);
      setCurrentFieldIndex(lastVisited.fieldIndex);
    } else if (currentFieldIndex > 0) {
      // Go back to previous field in current section
      setCurrentFieldIndex(currentFieldIndex - 1);
    } else if (currentSectionIndex > 0) {
      // Go to previous section (and show its last field)
      const prevSectionIndex = currentSectionIndex - 1;
      const prevSection = sections[prevSectionIndex];
      const lastFieldIndexInPrevSection = (prevSection?.fields?.length || 1) - 1;
      setCurrentSectionIndex(prevSectionIndex);
      setCurrentFieldIndex(lastFieldIndexInPrevSection);
    }
  };

  // Handle restart - completely reload the dialog from the beginning
  const handleRestart = () => {
    // Reset to initial dialog context
    setActiveDialogContext({
      sections: initialSections,
      speakerMode: initialSpeakerMode,
      speakerTypeId: initialSpeakerTypeId || null,
      speakerTemplateIds: initialSpeakerTemplateIds || [],
      dialogVariables: initialDialogVariables || [],
    });
    
    reachedViaJump.current = false;
    setCurrentSectionIndex(0);
    setVisitedSections([0]);
    setIsComplete(false);
    setVisitedFields([]);
    setCurrentFieldIndex(0);
    
    // Initialize variables from declarations
    const initialVars: Record<string, boolean | number> = {};
    if (initialDialogVariables) {
      initialDialogVariables.forEach(v => {
        initialVars[v.name] = v.initialValue;
      });
    }
    setVariables(initialVars);
    
    // Scroll to top immediately
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-background">
      {/* Loading overlay */}
      {isLoadingDialog && (
        <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="text-center space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-sm text-muted-foreground">Loading dialog...</p>
          </div>
        </div>
      )}
      
      {/* Main content area - centered with max width */}
      <div 
        ref={contentRef} 
        className="flex-1 overflow-y-auto py-8"
        style={{ paddingBottom: !isComplete ? '80px' : '32px' }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="space-y-4">
            {/* Render all visited fields */}
            {visitedFields.map((visited, index) => {
              // Check if this field is from a different section than the previous one
              const prevField = index > 0 ? visitedFields[index - 1] : null;
              const isNewSection = prevField && prevField.sectionIndex !== visited.sectionIndex;
              
              return (
                <React.Fragment key={`visited-${index}-${visited.sectionIndex}-${visited.fieldIndex}-${visited.field.id}`}>
                  {/* Add horizontal line when entering a new section */}
                  {isNewSection && (
                    <div className="border-t border-gray-300 my-6" />
                  )}
                  <div className="text-left opacity-70">
                    <DialogFieldPrevDisplay
                      field={visited.field}
                      sectionIndex={visited.sectionIndex}
                      speakerMode={speakerMode}
                      speakerTypeId={speakerTypeId}
                      gameId={gameId}
                      onUpdateField={onUpdateField}
                      conditionResult={visited.conditionResult}
                      setterResult={visited.setterResult}
                    />
                  </div>
                </React.Fragment>
              );
            })}

            {/* Add separator before current field if it's from a new section */}
            {currentField && visitedFields.length > 0 && 
             visitedFields[visitedFields.length - 1].sectionIndex !== currentSectionIndex && (
              <div className="border-t border-gray-300 my-6" />
            )}

            {/* Current field with smooth entry animation */}
            {!isComplete && currentField ? (
              <div 
                key={`current-${currentSectionIndex}-${currentFieldIndex}-${currentField.id}`}
                className={animationEnabled ? 'animate-in fade-in slide-in-from-bottom-4 duration-500' : 'animate-in fade-in duration-300'}
              >
                <DialogFieldPlayDisplay
                  field={currentField}
                  sectionIndex={currentSectionIndex}
                  fieldIndex={currentFieldIndex}
                  speakerMode={speakerMode}
                  speakerTypeId={speakerTypeId}
                  speakerTemplateIds={speakerTemplateIds}
                  gameId={gameId}
                  animationEnabled={animationEnabled}
                  onUpdateField={onUpdateField}
                  onOptionClick={handleOptionClick}
                  showOptions={currentField.type === 'question'}
                  evaluateCondition={evaluateCondition}
                  variables={variables}
                  dialogVariables={dialogVariables}
                  onTypingComplete={() => {
                    setIsCurrentFieldTyping(false);
                    // If this is an end-text or end-dialog field, automatically complete the dialog after typing
                    if (currentField.type === 'end-text' || currentField.type === 'end-dialog') {
                      // Add current field to visited fields first
                      if (currentField && currentSection) {
                        setVisitedFields(prev => [...prev, {
                          sectionIndex: currentSectionIndex,
                          fieldIndex: currentFieldIndex,
                          section: currentSection,
                          field: currentField,
                        }]);
                      }
                      // Then complete the dialog
                      setTimeout(() => setIsComplete(true), 500);
                    } else if (currentField.type === 'condition') {
                      // Handle condition field - evaluate and branch
                      setTimeout(async () => {
                        let nextDialog: string | null | undefined = null;
                        let nextDialogSection: string | null | undefined = null;
                        let nextSection: string | null | undefined = null;
                        let conditionResult: ConditionEvalResult | undefined = undefined;
                        
                        // New system: use conditionOptions
                        if (currentField.conditionOptions && currentField.conditionOptions.length > 0 && currentField.condition) {
                          // Determine which value to check:
                          // - For range conditions with checkVariable, use the checkVariable's value (e.g., Points = 100)
                          // - Otherwise, use the condition variable's value directly
                          const checkVarName = currentField.condition.checkVariable || currentField.condition.variable;
                          
                          // Check if the variable to evaluate exists
                          if (!(checkVarName in variablesRef.current)) {
                            toast({
                              title: 'Variable not initialized',
                              description: `Variable "${checkVarName}" is not initialized. Check your variable declarations.`,
                              variant: 'destructive',
                            });
                            handleNextField();
                            return;
                          }
                          
                          const variableValue = variablesRef.current[checkVarName];
                          
                          // Get the variable declaration for range lookups
                          const varDeclaration = dialogVariables?.find(v => v.name === currentField.condition!.variable);
                          
                          // Find the first matching condition option
                          const matchingOption = currentField.conditionOptions.find(opt => {
                            const operator = opt.operator || '==';
                            switch (operator) {
                              case '==':
                                return variableValue === opt.condition_value;
                              case '!=':
                                return variableValue !== opt.condition_value;
                              case '>':
                                return Number(variableValue) > Number(opt.condition_value);
                              case '<':
                                return Number(variableValue) < Number(opt.condition_value);
                              case '>=':
                                return Number(variableValue) >= Number(opt.condition_value);
                              case '<=':
                                return Number(variableValue) <= Number(opt.condition_value);
                              case 'in_range': {
                                // Range check: condition_value is the elementId of a range in the TypeStore
                                if (!varDeclaration?.typeStoreRef?.typeId) {
                                  console.warn(`Range variable "${currentField.condition!.variable}" has no TypeStore reference`);
                                  return false;
                                }
                                const typeItem = getTypeItemById(varDeclaration.typeStoreRef.typeId);
                                if (!typeItem?.data) {
                                  console.warn(`TypeItem ${varDeclaration.typeStoreRef.typeId} not found`);
                                  return false;
                                }
                                const rangeElement = typeItem.data.find(el => el.id === String(opt.condition_value));
                                if (!rangeElement) {
                                  console.warn(`Range element ${opt.condition_value} not found in TypeItem`);
                                  return false;
                                }
                                return isValueInRange(Number(variableValue), rangeElement);
                              }
                              default:
                                return false;
                            }
                          });
                          
                          // Build condition evaluation result for display
                          // For range conditions, resolve the element ID to a human-readable name
                          let matchedLabel: string | undefined = undefined;
                          if (matchingOption?.operator === 'in_range' && varDeclaration?.typeStoreRef?.typeId) {
                            const typeItem = getTypeItemById(varDeclaration.typeStoreRef.typeId);
                            const rangeEl = typeItem?.data?.find(el => el.id === String(matchingOption.condition_value));
                            matchedLabel = rangeEl?.name;
                          }
                          
                          conditionResult = {
                            variableName: checkVarName,
                            variableValue: variableValue,
                            matchedOperator: matchingOption ? (matchingOption.operator || '==') : undefined,
                            matchedValue: matchingOption?.condition_value,
                            matchedLabel,
                            matched: !!matchingOption,
                          };
                          
                          if (matchingOption) {
                            nextDialog = matchingOption.next_dialog;
                            nextDialogSection = matchingOption.next_dialog_section;
                            nextSection = matchingOption.next_section;
                          }
                        } 
                        // Legacy system: use next_section_true/false
                        // This handles old dialogs that haven't been migrated to conditionOptions yet
                        else if (currentField.condition && (currentField.next_section_true !== undefined || currentField.next_section_false !== undefined)) {
                          if (!(currentField.condition.variable in variablesRef.current)) {
                            toast({
                              title: 'Variable not initialized',
                              description: `Variable "${currentField.condition.variable}" is not initialized. Check your variable declarations.`,
                              variant: 'destructive',
                            });
                            handleNextField();
                            return;
                          }
                          
                          // For legacy conditions, assume boolean check (true/false branches)
                          const variableValue = variablesRef.current[currentField.condition.variable];
                          const legacyResult = Boolean(variableValue);
                          
                          conditionResult = {
                            variableName: currentField.condition.variable,
                            variableValue: variableValue,
                            matchedOperator: '==',
                            matchedValue: legacyResult,
                            matched: true,
                          };
                          
                          nextDialog = legacyResult ? currentField.next_dialog_true : currentField.next_dialog_false;
                          nextDialogSection = legacyResult ? currentField.next_dialog_section_true : currentField.next_dialog_section_false;
                          nextSection = legacyResult ? currentField.next_section_true : currentField.next_section_false;
                        }
                        
                        // Helper to build visited field with condition result
                        const buildVisitedField = (): VisitedField => ({
                          sectionIndex: currentSectionIndex,
                          fieldIndex: currentFieldIndex,
                          section: currentSection,
                          field: currentField,
                          conditionResult,
                        });
                        
                        // Check if condition jumps to external dialog
                        if (nextDialog) {
                            // Add current field to visited fields before jumping
                            if (currentField && currentSection) {
                              setVisitedFields(prev => [...prev, buildVisitedField()]);
                            }
                            
                            try {
                              setIsLoadingDialog(true);
                              
                              if (!gameId) {
                                throw new Error('Game ID is required to load external dialogs');
                              }
                              
                              // Fetch the target dialog
                              const targetDialog = await fetchDialog(nextDialog, gameId);
                              const dialogName = targetDialog?.name || 'Unknown Dialog';
                              
                              // Update sections from the new dialog
                              let newSections = targetDialog.data?.sections || [];
                              
                              // Ensure sections have stable IDs
                              newSections = newSections.map((section, index) => ({
                                ...section,
                                id: section.id || `section_${Date.now()}_${index}`,
                              }));
                              
                              // Find target section index
                              let targetSectionIndex = 0;
                              if (nextDialogSection) {
                                const foundIndex = newSections.findIndex(s => s.section_identifier === nextDialogSection);
                                if (foundIndex !== -1) {
                                  targetSectionIndex = foundIndex;
                                }
                              }
                              
                              // Update active dialog context for play mode only
                              setActiveDialogContext({
                                sections: newSections,
                                speakerMode: targetDialog.data?.speakerMode || 'none',
                                speakerTypeId: targetDialog.data?.speakerTypeId || null,
                                speakerTemplateIds: targetDialog.data?.speakerTemplateIds || [],
                                dialogVariables: targetDialog.data?.variables || [],
                                dialogId: nextDialog,
                                dialogName: dialogName,
                              });
                              
                              // Keep history for fluent conversation - only reset current position
                              reachedViaJump.current = true;
                              setCurrentSectionIndex(targetSectionIndex);
                              setCurrentFieldIndex(0);
                              setIsComplete(false);
                            } catch (error) {
                              console.error('Error loading external dialog:', error);
                              toast({
                                title: 'Error',
                                description: 'Failed to load next dialog',
                                variant: 'destructive',
                              });
                            } finally {
                              setIsLoadingDialog(false);
                            }
                            return;
                          }
                        
                        // Check if condition jumps to a section
                        if (nextSection) {
                          // Find the target section
                          const targetSectionIndex = sections.findIndex(
                            s => s.section_identifier === nextSection
                          );
                          
                          if (targetSectionIndex !== -1) {
                            // Add current field to visited fields before jumping
                            if (currentField && currentSection) {
                              setVisitedFields(prev => [...prev, buildVisitedField()]);
                            }
                            
                            reachedViaJump.current = true;
                            setCurrentSectionIndex(targetSectionIndex);
                            setCurrentFieldIndex(0);
                            setIsCurrentFieldTyping(true);
                          } else {
                            // Section not found - continue to next field
                            handleNextField();
                          }
                          return;
                        }
                        
                        // No branch defined or no condition - continue to next field
                        handleNextField();
                      }, 800);
                    } else if (currentField.type === 'setter') {
                      // Handle setter field - execute variable action and continue
                      setTimeout(async () => {
                        let setterResult: SetterEvalResult | undefined = undefined;
                        
                        if (currentField.setter) {
                          // Execute the setter action
                          executeVariableAction({
                            variable: currentField.setter.variable,
                            operator: currentField.setter.operator,
                            value: currentField.setter.value,
                          });
                          
                          // Build setter result for display
                          setterResult = {
                            variableName: currentField.setter.variable,
                            operator: currentField.setter.operator,
                            setValue: currentField.setter.value,
                            newValue: variablesRef.current[currentField.setter.variable],
                          };
                        }
                        
                        // Add current field to visited fields
                        if (currentField && currentSection) {
                          setVisitedFields(prev => [...prev, {
                            sectionIndex: currentSectionIndex,
                            fieldIndex: currentFieldIndex,
                            section: currentSection,
                            field: currentField,
                            setterResult,
                          }]);
                        }
                        
                        // Check if setter jumps to external dialog
                        if (currentField.next_dialog) {
                          try {
                            setIsLoadingDialog(true);
                            
                            if (!gameId) {
                              throw new Error('Game ID is required to load external dialogs');
                            }
                            
                            const targetDialog = await fetchDialog(currentField.next_dialog, gameId);
                            const dialogName = targetDialog?.name || 'Unknown Dialog';
                            
                            let newSections = targetDialog.data?.sections || [];
                            newSections = newSections.map((section, index) => ({
                              ...section,
                              id: section.id || `section_${Date.now()}_${index}`,
                            }));
                            
                            let targetSectionIndex = 0;
                            if (currentField.next_dialog_section) {
                              const foundIndex = newSections.findIndex(s => s.section_identifier === currentField.next_dialog_section);
                              if (foundIndex !== -1) {
                                targetSectionIndex = foundIndex;
                              }
                            }
                            
                            setActiveDialogContext({
                              sections: newSections,
                              speakerMode: targetDialog.data?.speakerMode || 'none',
                              speakerTypeId: targetDialog.data?.speakerTypeId || null,
                              speakerTemplateIds: targetDialog.data?.speakerTemplateIds || [],
                              dialogVariables: targetDialog.data?.variables || [],
                              dialogId: currentField.next_dialog,
                              dialogName: dialogName,
                            });
                            
                            reachedViaJump.current = true;
                            setCurrentSectionIndex(targetSectionIndex);
                            setCurrentFieldIndex(0);
                            setIsComplete(false);
                          } catch (error) {
                            console.error('Error loading external dialog:', error);
                            toast({
                              title: 'Error',
                              description: 'Failed to load next dialog',
                              variant: 'destructive',
                            });
                          } finally {
                            setIsLoadingDialog(false);
                          }
                          return;
                        }
                        
                        // Check if setter jumps to a section (branching)
                        if (currentField.next_section) {
                          const targetSectionIndex = sections.findIndex(
                            s => s.section_identifier === currentField.next_section
                          );
                          
                          if (targetSectionIndex !== -1) {
                            reachedViaJump.current = true;
                            setCurrentSectionIndex(targetSectionIndex);
                            setCurrentFieldIndex(0);
                            setIsCurrentFieldTyping(true);
                            return;
                          }
                        }
                        
                        // No jump defined - continue to next field linearly
                        handleNextField();
                      }, 300);
                    } else if (currentField.type !== 'question') {
                      // For regular dialog fields, check for field-level jumps
                      setTimeout(async () => {
                        // Check if field jumps to external dialog
                        if (currentField.next_dialog) {
                          // Add current field to visited fields before jumping
                          if (currentField && currentSection) {
                            setVisitedFields(prev => [...prev, {
                              sectionIndex: currentSectionIndex,
                              fieldIndex: currentFieldIndex,
                              section: currentSection,
                              field: currentField,
                            }]);
                          }
                          
                          try {
                            setIsLoadingDialog(true);
                            
                            if (!gameId) {
                              throw new Error('Game ID is required to load external dialogs');
                            }
                            
                            // Fetch the target dialog
                            const targetDialog = await fetchDialog(currentField.next_dialog, gameId);
                            const dialogName = targetDialog?.name || 'Unknown Dialog';
                            
                            // Update sections from the new dialog
                            let newSections = targetDialog.data?.sections || [];
                            
                            // Ensure sections have stable IDs
                            newSections = newSections.map((section, index) => ({
                              ...section,
                              id: section.id || `section_${Date.now()}_${index}`,
                            }));
                            
                            // Find target section index
                            let targetSectionIndex = 0;
                            if (currentField.next_dialog_section) {
                              const foundIndex = newSections.findIndex(s => s.section_identifier === currentField.next_dialog_section);
                              if (foundIndex !== -1) {
                                targetSectionIndex = foundIndex;
                              }
                            }
                            
                            // Update active dialog context for play mode only
                            setActiveDialogContext({
                              sections: newSections,
                              speakerMode: targetDialog.data?.speakerMode || 'none',
                              speakerTypeId: targetDialog.data?.speakerTypeId || null,
                              speakerTemplateIds: targetDialog.data?.speakerTemplateIds || [],
                              dialogVariables: targetDialog.data?.variables || [],
                              dialogId: currentField.next_dialog,
                              dialogName: dialogName,
                            });
                            
                            // Keep history for fluent conversation - only reset current position
                            reachedViaJump.current = true;
                            setCurrentSectionIndex(targetSectionIndex);
                            setCurrentFieldIndex(0);
                            setIsComplete(false);
                          } catch (error) {
                            console.error('Error loading external dialog:', error);
                            toast({
                              title: 'Error',
                              description: 'Failed to load next dialog',
                              variant: 'destructive',
                            });
                          } finally {
                            setIsLoadingDialog(false);
                          }
                          return;
                        }
                        
                        // Check if field jumps to a section
                        if (currentField.next_section) {
                          // Find the target section
                          const targetSectionIndex = sections.findIndex(
                            s => s.section_identifier === currentField.next_section
                          );
                          
                          if (targetSectionIndex !== -1) {
                            // Add current field to visited fields before jumping
                            if (currentField && currentSection) {
                              setVisitedFields(prev => [...prev, {
                                sectionIndex: currentSectionIndex,
                                fieldIndex: currentFieldIndex,
                                section: currentSection,
                                field: currentField,
                              }]);
                            }
                            
                            reachedViaJump.current = true;
                            setCurrentSectionIndex(targetSectionIndex);
                            setCurrentFieldIndex(0);
                            setIsCurrentFieldTyping(true);
                          } else {
                            // Section not found - continue to next field
                            handleNextField();
                          }
                          return;
                        }
                        
                        // No jump - continue to next field normally
                        handleNextField();
                      }, 800);
                    }
                    // For question fields, do nothing - wait for user to select an option
                  }}
                />
              </div>
            ) : !currentField && !isComplete ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No content in this section</p>
              </div>
            ) : null}

            {/* End of dialog message with fade in animation */}
            {isComplete && (
              <div className={`text-center pt-8 space-y-6 ${animationEnabled ? 'animate-in fade-in zoom-in-95 duration-700' : 'animate-in fade-in duration-400'}`}>
                <div className="text-2xl font-bold text-gray-800">
                  End of Dialog
                </div>
                <button
                  onClick={handleRestart}
                  className="text-gray-500 hover:text-gray-900 hover:underline transition-all duration-200"
                  type="button"
                >
                  Replay
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Variables debug panel */}
      {showVariablesDebug && Object.keys(variables).length > 0 && (
        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-3">
          <div className="max-w-[900px] mx-auto">
            <div className="text-xs font-semibold text-gray-700 mb-2">Variables (Debug)</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(variables).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 px-3 py-1 bg-white border border-gray-300 rounded text-xs">
                  <span className="font-mono text-gray-600">{key}:</span>
                  <span className="font-mono font-bold text-blue-600">
                    {typeof value === 'boolean' ? (value ? 'true' : 'false') : value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Navigation bar at bottom - contained within component */}
      {!isComplete && (
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4">
          <div className="max-w-[900px] mx-auto flex items-center justify-between gap-4">
            {/* Back button */}
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentSectionIndex === 0 && currentFieldIndex === 0}
              className="px-4"
              type="button"
            >
              <IconProvider iconstring="fa-arrow-left" className="mr-2" size={14} />
              Back
            </Button>
            
            {/* Animation toggle */}
            <button
              onClick={() => setAnimationEnabled(!animationEnabled)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-gray-100 transition-colors text-sm text-gray-600"
              title={animationEnabled ? "Disable typing animation" : "Enable typing animation"}
              type="button"
            >
              <IconProvider 
                iconstring={animationEnabled ? "fa-bolt" : "fa-bolt-slash"} 
                size={14} 
              />
              <span className="text-xs hidden sm:inline">
                {animationEnabled ? "Animated" : "Instant"}
              </span>
            </button>

            {/* Variables debug toggle */}
            {Object.keys(variables).length > 0 && (
              <button
                onClick={() => setShowVariablesDebug(!showVariablesDebug)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-gray-100 transition-colors text-sm text-gray-600"
                title="Toggle variables debug panel"
                type="button"
              >
                <IconProvider 
                  iconstring="fa-flask" 
                  size={14} 
                />
                <span className="text-xs hidden sm:inline">
                  Variables {showVariablesDebug ? '▼' : '▶'}
                </span>
              </button>
            )}

            {/* Section progress dots */}
            <div className="flex items-center gap-1.5">
              {sections.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    reachedViaJump.current = false; // manual navigation resets jump tracking
                    setCurrentSectionIndex(index);
                    setVisitedFields([]);
                    setCurrentFieldIndex(0);
                    if (!visitedSections.includes(index)) {
                      setVisitedSections(prev => [...prev, index]);
                    }
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSectionIndex
                      ? 'bg-blue-600 w-3 h-3'
                      : visitedSections.includes(index)
                      ? 'bg-blue-300 hover:bg-blue-400'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  title={`Go to section ${index + 1}`}
                  type="button"
                />
              ))}
            </div>

            {/* Restart button */}
            <Button
              variant="ghost"
              onClick={handleRestart}
              className="px-4"
              type="button"
            >
              <IconProvider iconstring="fa-rotate-right" className="mr-2" size={14} />
              Restart
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


